import { Dtos, NoteTopicId, Relations } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminNoteTopicsQueryKey } from "../note.keys";
import { AdminNoteTopic } from "./types";

export const useUpdateAdminNoteTopic = () => {
	const queryClient = useQueryClient();
	const { patch } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationFn: async ({
			data,
			noteTopicId,
		}: {
			data: Dtos.Admin.AdminNoteTopic.Update.Request;
			noteTopicId: NoteTopicId;
			relation: Relations.Notes;
		}) => {
			return await patch<Dtos.Admin.AdminNoteTopic.Update.Response>(
				`/v2/admin/admin-notes/note-topics/${noteTopicId}`,
				data,
			);
		},
		onMutate: async ({
			data,
			noteTopicId,
			relation,
		}: {
			data: Dtos.Admin.AdminNoteTopic.Update.Request;
			noteTopicId: NoteTopicId;
			relation: Relations.Notes;
		}) => {
			await queryClient.cancelQueries({
				queryKey: adminNoteTopicsQueryKey(relation),
			});
			const previousTopics = queryClient.getQueryData<AdminNoteTopic[]>(
				adminNoteTopicsQueryKey(relation),
			);
			queryClient.setQueryData<AdminNoteTopic[]>(adminNoteTopicsQueryKey(relation), (old) => {
				if (!old) return [];
				return old.map((topic) => (topic.id === noteTopicId ? { ...topic, ...data } : topic));
			});
			return { previousTopics, relation };
		},
		onSuccess(result, { relation }) {
			if (result) {
				queryClient.setQueryData<AdminNoteTopic[]>(adminNoteTopicsQueryKey(relation), (old) => {
					if (!old) return [];
					return old.map((topic) => (topic.id === result.id ? { ...topic, ...result } : topic));
				});
			}
		},
		onError: (error: Error, _inputData, context) => {
			if (context?.previousTopics !== undefined) {
				queryClient.setQueryData(adminNoteTopicsQueryKey(context.relation), context.previousTopics);
			}
			triggerErrorModal(error);
		},
	});

	return {
		updateAdminNoteTopic: mutation.mutate,
		updateAdminNoteTopicIsLoading: mutation.isPending,
	};
};
